import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import VantaBackground from './componets/vantabackground';


const ProTraderMobileApp = () => {
  // Mobile app features - focused on AI, risk, and prop firm
  const appFeatures = [
    { icon: '🤖', title: 'AI Analytics Engine', desc: 'Pattern recognition, strategy analysis & performance predictions' },
    { icon: '⚠️', title: 'Smart Risk Management', desc: 'Real-time drawdown monitoring with automated alerts' },
    { icon: '🏢', title: 'Prop Firm Ready', desc: 'Built-in compliance, daily loss limits & rule enforcement' },
    { icon: '📊', title: 'Real-Time Dashboard', desc: 'Track balance, equity, P&L, and 15+ performance metrics' },
    { icon: '📈', title: 'Advanced Analytics', desc: 'Sharpe ratio, profit factor, expectancy & equity curves' },
    { icon: '🎯', title: 'Goal Tracking', desc: 'Set profit targets with milestone tracking & progress alerts' },
    { icon: '🧠', title: 'Psychology Tracking', desc: 'Correlate mood patterns with trading performance' },
    { icon: '📱', title: 'Remote Control', desc: 'Telegram integration for emergency position management' }
  ];

  return (
    <>
      <style>{`
        /* Container styling */
        .protrader-mobile-container {
          position: relative;
          min-height: 100vh;
          width: 100%;
        }

        /* Fix hamburger button size */
        .hamburger-btn {
          width: 40px !important;
          height: 40px !important;
          padding: 8px !important;
        }

        .hamburger-icon {
          width: 20px !important;
          height: 15px !important;
        }

        .hamburger-icon span {
          height: 2px !important;
        }

        /* Fix select dropdown visibility */
        .glass-input select {
          color: white !important;
          background: rgba(255, 255, 255, 0.1) !important;
        }

        .glass-input select option {
          background: rgba(0, 0, 0, 0.95) !important;
          color: white !important;
          padding: 10px !important;
          border: none !important;
        }

        .glass-input select option:hover,
        .glass-input select option:focus {
          background: rgba(66, 126, 234, 0.8) !important;
          color: white !important;
        }

        select {
          color: white !important;
        }

        select option {
          background: rgba(0, 0, 0, 0.95) !important;
          color: white !important;
          padding: 8px 12px !important;
        }

        select option:hover,
        select option:checked {
          background: rgba(66, 126, 234, 0.8) !important;
          color: white !important;
        }

        /* Phone mockup styles */
        .phone-mockup {
          position: relative;
          width: 280px;
          height: 560px;
          background: linear-gradient(145deg, #1a1a2e, #16213e);
          border-radius: 36px;
          padding: 12px;
          box-shadow:
            0 0 60px rgba(66, 126, 234, 0.3),
            inset 0 0 20px rgba(0, 0, 0, 0.5);
          border: 2px solid rgba(66, 126, 234, 0.2);
        }

        .phone-screen {
          width: 100%;
          height: 100%;
          background: linear-gradient(to bottom, #0a0a0a, #1a1a2e);
          border-radius: 28px;
          overflow: hidden;
          position: relative;
        }

        .phone-notch {
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 140px;
          height: 28px;
          background: #000;
          border-radius: 0 0 20px 20px;
          z-index: 10;
        }

        .phone-content {
          padding: 40px 16px 16px 16px;
          height: 100%;
          overflow-y: auto;
        }

        /* Feature grid for mobile */
        @media (max-width: 768px) {
          .phone-mockup {
            width: 240px;
            height: 480px;
          }

          .hamburger-btn {
            width: 36px !important;
            height: 36px !important;
            padding: 6px !important;
          }

          .hamburger-icon {
            width: 18px !important;
            height: 14px !important;
          }
        }

        /* Smooth scroll for phone content */
        .phone-content::-webkit-scrollbar {
          width: 2px;
        }

        .phone-content::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
        }

        .phone-content::-webkit-scrollbar-thumb {
          background: rgba(66, 126, 234, 0.3);
          border-radius: 10px;
        }
      `}</style>

      <div className="protrader-mobile-container relative w-full font-sans">
        {/* Vanta Background */}
        <div className="fixed inset-0 z-0" style={{ pointerEvents: 'none' }}>
          <VantaBackground
            color={0x6a11cb}
            backgroundColor={0x000000}
            scale={1.4}
            scaleMobile={1.0}
          />
        </div>

        {/* Gradient Overlay */}
        <div className="fixed inset-0 z-[1] bg-gradient-to-b from-black/80 via-black/70 to-black/80" />

        {/* Additional overlay for better text contrast */}
        <div className="fixed inset-0 z-[2] bg-black/40" />

        {/* Back Button */}
        <Link
          to="/free-tools"
          className="fixed top-24 left-4 z-[999] glass-btn glass-btn-secondary px-3 py-2 text-sm flex items-center gap-2 hover:scale-105 transition-transform"
        >
          <span>←</span>
          <span className="hidden sm:inline">Back to Tools</span>
          <span className="sm:hidden">Back</span>
        </Link>

        {/* Main Content */}
        <div className="relative z-[10] min-h-full pt-32 pb-32 px-4">
          <div className="max-w-7xl mx-auto">

            {/* Hero Section with Phone Mockup */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-16">

              {/* Left Side - App Info */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center lg:text-left order-2 lg:order-1"
              >
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-2 rounded-full text-white font-bold mb-6">
                  <span className="text-xl">🎁</span>
                  <span>100% FREE APP</span>
                </div>

                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent font-orbitron leading-tight">
                  ProTrader AI Mobile App
                </h1>

                <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-6 text-left" style={{textShadow: '0 1px 2px rgba(0,0,0,0.6)'}}>
                  AI-powered trading journal with military-grade risk management. Perfect for prop firm traders and serious retail traders.
                </p>

                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 lg:p-6 backdrop-blur-sm mb-6">
                  <h3 className="text-base sm:text-lg font-semibold text-purple-400 mb-3">🚀 Built for Prop Traders & Risk Management:</h3>
                  <div className="text-gray-300 text-sm lg:text-base space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-green-400">🤖</span>
                      <span>AI-powered insights & pattern recognition</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-400">⚠️</span>
                      <span>Real-time drawdown protection & alerts</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-400">🏢</span>
                      <span>Automated prop firm rule compliance</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-400">💎</span>
                      <span>Worth $197 - Yours FREE!</span>
                    </div>
                  </div>
                </div>

                {/* Quick Features List */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-300">
                    <span className="text-green-400">🤖</span>
                    <span>AI analytics engine</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <span className="text-green-400">⚠️</span>
                    <span>Risk management tools</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <span className="text-green-400">🏢</span>
                    <span>Prop firm compliance</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <span className="text-green-400">📊</span>
                    <span>15+ performance metrics</span>
                  </div>
                </div>
              </motion.div>

              {/* Right Side - Phone Mockup */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="flex justify-center order-1 lg:order-2"
              >
                <div className="phone-mockup">
                  <div className="phone-screen">
                    <div className="phone-notch"></div>
                    <div className="phone-content">
                      {/* Mini Dashboard Preview */}
                      <div className="text-center mb-4">
                        <div className="text-xs text-purple-400 mb-1">ProTrader</div>
                        <div className="text-2xl font-bold text-white">$12,456</div>
                        <div className="text-xs text-green-400">+$234 (1.9%)</div>
                      </div>

                      {/* Quick Stats */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        <div className="bg-white/5 rounded-lg p-2 backdrop-blur-sm">
                          <div className="text-xs text-gray-400">Win Rate</div>
                          <div className="text-sm font-bold text-green-400">68%</div>
                        </div>
                        <div className="bg-white/5 rounded-lg p-2 backdrop-blur-sm">
                          <div className="text-xs text-gray-400">Trades</div>
                          <div className="text-sm font-bold text-white">142</div>
                        </div>
                      </div>

                      {/* Mini Chart */}
                      <div className="bg-white/5 rounded-lg p-2 mb-4 backdrop-blur-sm">
                        <div className="text-xs text-gray-400 mb-2">Equity Curve</div>
                        <svg viewBox="0 0 200 60" className="w-full h-12">
                          <path
                            d="M 0 50 Q 50 45 100 30 T 200 10"
                            fill="none"
                            stroke="url(#gradient)"
                            strokeWidth="2"
                          />
                          <defs>
                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="#a855f7" />
                              <stop offset="100%" stopColor="#ec4899" />
                            </linearGradient>
                          </defs>
                        </svg>
                      </div>

                      {/* Bottom Navigation */}
                      <div className="grid grid-cols-5 gap-1 bg-white/5 rounded-lg p-2 backdrop-blur-sm">
                        {['📊', '📅', '📈', '🎯', '⚙️'].map((icon, i) => (
                          <div key={i} className="text-center text-xs py-1">
                            <div className="text-lg">{icon}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mb-16"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 text-center">
                AI-Powered Risk Management for Prop Traders
              </h2>
              <p className="text-base sm:text-lg text-gray-400 mb-8 text-center max-w-3xl mx-auto">
                Institutional-level tools typically found in $10k+ platforms - now available for free
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {appFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="glass-card p-4 text-center hover:scale-105 transition-transform"
                  >
                    <div className="text-4xl mb-3">{feature.icon}</div>
                    <h3 className="text-base font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-xs text-gray-400">{feature.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Signup CTA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="max-w-2xl mx-auto"
            >
              <div className="glass-card glass-card-large">
                <div className="text-center mb-6">
                  <h2 className="glass-card-title text-2xl sm:text-3xl mb-2">
                    Get Your Free Mobile App
                  </h2>
                  <p className="glass-card-text">
                    Sign up on our dedicated ProTrader platform
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 sm:p-6 backdrop-blur-sm">
                    <h3 className="text-base sm:text-lg font-semibold text-purple-400 mb-3">What Happens Next:</h3>
                    <div className="text-gray-300 text-sm sm:text-base space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="text-green-400 mt-0.5">1️⃣</span>
                        <span>Sign up on the ProTrader platform</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-green-400 mt-0.5">2️⃣</span>
                        <span>Choose your preferred broker</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-green-400 mt-0.5">3️⃣</span>
                        <span>Get instant access to your free mobile app</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-green-400 mt-0.5">4️⃣</span>
                        <span>Download and start tracking your trades</span>
                      </div>
                    </div>
                  </div>

                  {/* Sign Up Button */}
                  <motion.a
                    href="https://protrader.ksquants.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="glass-btn glass-btn-primary w-full text-base py-4 block text-center"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <span className="text-xl">🚀</span>
                      Sign Up for ProTrader
                    </span>
                  </motion.a>

                  <div className="text-center">
                    <p className="glass-card-text text-xs">
                      🔒 Secure signup on our dedicated ProTrader platform
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProTraderMobileApp;
